"use client";

import { Button } from "@bandwith/ui";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { type FileRejection, useDropzone } from "react-dropzone";
import { MAX_AUDIO_FILE_SIZE } from "./constants";

type Props = {
  onFileAdd: (file: File) => void;
  disabled?: boolean;
};

function formatFileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)}MB`;
}

export function FileInput({ onFileAdd, disabled }: Props) {
  const [error, setError] = useState<string | null>(null);

  // Auto-clear error after 5 seconds with proper cleanup on unmount
  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timeout);
  }, [error]);

  const handleDropRejected = (rejections: FileRejection[]) => {
    const rejection = rejections[0];
    if (!rejection) return;

    const errorCode = rejection.errors[0]?.code;
    if (errorCode === "file-too-large") {
      setError(
        `File too large. Max size is ${formatFileSize(MAX_AUDIO_FILE_SIZE)}`,
      );
    } else if (errorCode === "file-invalid-type") {
      setError("Invalid file type. Please upload an audio file.");
    } else {
      setError("File upload failed. Please try again.");
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setError(null);
      if (acceptedFiles[0]) {
        onFileAdd(acceptedFiles[0]);
      }
    },
    onDropRejected: handleDropRejected,
    accept: {
      "audio/*": [".mp3", ".m4a", ".wav", ".ogg", ".webm", ".aac"],
    },
    maxSize: MAX_AUDIO_FILE_SIZE,
    multiple: false,
    disabled,
  });

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="secondary"
        {...getRootProps()}
        disabled={disabled}
        leftIcon={Plus}
        className={`w-full justify-center ${
          isDragActive
            ? "border-amber bg-amber/10 text-amber"
            : error
              ? "border-red-500 bg-red-500/10 text-red-500"
              : ""
        }`}
      >
        <input {...getInputProps()} />
        {isDragActive ? "Drop Here" : "Upload"}
      </Button>
      {error && (
        <p className="text-xs text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
